import { useCallback, useEffect, useState } from "react";
import {
  ArrowLeft,
  CalendarClock,
  ChevronRight,
  FileBadge,
  FileText,
  FlaskConical,
  Plus,
  Search,
  WalletCards,
  X,
} from "lucide-react";
import { api, type Certification, type CertificationDetail } from "./api";
import { KanbanBoard } from "./kanban-board";
import {useCertificationWorkflowKanban,useWorkflowKanbanColumns,workflowColumnForRow} from "./certification-workflow-kanban";
import {
  MultiSelectFilter,
  TableDisplayControls,
  useTablePreferences,
} from "./table-controls";
import { DatePicker } from "./date-picker";
import { CertificationEntityResourcesPanel } from "./certification-entity-resources";
import {
  CertificationAttachmentPicker,
  uploadCertificationAttachments,
} from "./certification-attachment-picker";

const statusLabels: Record<string, string> = {
  PLANEJADA: "Planejada",
  "EM PREPARAÇÃO": "Em preparação",
  "EM AUDITORIA": "Em auditoria",
  "AGUARDANDO RESULTADO": "Aguardando resultado",
  ATIVA: "Ativa",
  "A VENCER": "A vencer",
  VENCIDA: "Vencida",
  SUSPENSA: "Suspensa",
  CANCELADA: "Cancelada",
  "EM RENOVAÇÃO": "Em renovação",
};
const stageLabels: Record<string, string> = {
  PENDENTE: "Pendente",
  "EM ANDAMENTO": "Em andamento",
  BLOQUEADA: "Bloqueada",
  CONCLUÍDA: "Concluída",
  CANCELADA: "Cancelada",
};
const formatDate = (value?: string) =>
  value
    ? new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR")
    : "Não informada";
const money = (value = 0, currency = "BRL") =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(value);
const tone = (status: string) =>
  status === "ATIVA" || status === "CONCLUÍDA"
    ? "success"
    : status.includes("VENC") || status === "BLOQUEADA"
      ? "danger"
      : status.includes("AGUARDANDO") || status === "PENDENTE"
        ? "warning"
        : "info";
const certificationColumns = [
  { key: "code", label: "Código", required: true },
  { key: "certification", label: "Certificação" },
  { key: "status", label: "Status" },
  { key: "owner", label: "Responsável" },
  { key: "expires", label: "Validade" },
  { key: "preparation", label: "Preparação" },
  { key: "cost", label: "Custo realizado" },
];
export function CertificationsModule({
  onError,
}: {
  onError: (message: string) => void;
}) {
  const [data, setData] = useState<{
    items: Certification[];
    summary: any;
    simulated: boolean;
  }>();
  const [query, setQuery] = useState(""),
    [statuses, setStatuses] = useState<string[]>([]),
    [types, setTypes] = useState<string[]>([]),
    [selected, setSelected] = useState(""),
    [creating, setCreating] = useState(false);
  const workflowTemplate=useCertificationWorkflowKanban("certifications",onError);
  const workflowColumns=useWorkflowKanbanColumns(workflowTemplate);
  const preferences = useTablePreferences(
    "sgq:certifications-table",
    certificationColumns,
  );
  const load = useCallback(
    () =>
      api
        .certifications(query)
        .then(setData)
        .catch((error) => onError(error.message)),
    [query, onError],
  );
  useEffect(() => {
    const timeout = setTimeout(load, 180);
    return () => clearTimeout(timeout);
  }, [load]);
  if (selected)
    return (
      <CertificationDetailView
        id={selected}
        onBack={() => {
          setSelected("");
          load();
        }}
        onError={onError}
      />
    );
  if (!data||!workflowTemplate)
    return (
      <section className="card loading-card" aria-live="polite">
        Carregando certificações…
      </section>
    );
  const modelColumnFor=(item:Certification)=>workflowColumnForRow(workflowTemplate,item);
  const modeledItems=data.items.filter(item=>modelColumnFor(item));
  const visibleItems = modeledItems.filter((item) => (!statuses.length||statuses.includes(modelColumnFor(item)!.id))&&(!types.length || types.includes(item.type)));
  return (
    <section className="certifications-module">
      <section
        className={`card certification-list data-table-card ${preferences.className}`}
      >
        <div className="toolbar certification-toolbar table-toolbar">
          <div className="table-filter-group">
            <label className="search">
              <Search />
              <input
                aria-label="Buscar certificações"
                placeholder="Buscar código, certificação ou responsável"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            <MultiSelectFilter
              label="Todos os status"
              options={workflowTemplate.columns.map(column => ({
                value:column.id,
                label:column.name,
              }))}
              value={statuses}
              onChange={setStatuses}
            />
            <MultiSelectFilter
              label="Todos os tipos"
              options={Array.from(
                new Set(data.items.map((item) => item.type)),
              ).map((value) => ({ value, label: value }))}
              value={types}
              onChange={setTypes}
            />
          </div>
          <div className="table-toolbar-actions">
            <TableDisplayControls
              count={visibleItems.length}
              total={modeledItems.length}
              columns={certificationColumns}
              hidden={preferences.hidden}
              view={preferences.view}
              onView={preferences.setView}
              onToggle={preferences.toggle}
              onReset={preferences.reset}
            />
            <button className="button primary" onClick={() => setCreating(true)}>
              <Plus />
              Nova certificação
            </button>
          </div>
        </div>
        {preferences.view === "kanban" ? (
          <KanbanBoard
            columns={workflowColumns}
            rows={visibleItems}
            getStatus={(item) => modelColumnFor(item)?.id||""}
            onOpen={(item) => setSelected(item.id)}
            onStatusChange={async(item,columnId,comment)=>{try{await api.moveCertificationWorkflowKanbanItem("certifications",item.id,columnId,comment);await load()}catch(error:any){onError(error.message);throw error}}}
            renderCard={(item) => (
              <>
                <strong>{item.name}</strong>
                <span className="kanban-card-customer">
                  {item.internal_owner || "Responsável não informado"}
                </span>
                <span className="status-pill" style={{color:modelColumnFor(item)?.color,borderColor:modelColumnFor(item)?.color,backgroundColor:modelColumnFor(item)?.color?`color-mix(in srgb,${modelColumnFor(item)?.color} 12%,transparent)`:undefined}}>
                  {modelColumnFor(item)?.name}
                </span>
                <small>
                  {item.code} · Validade: {formatDate(item.expires_at)}
                </small>
              </>
            )}
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th data-column="code">Código</th>
                  <th data-column="certification">Certificação</th>
                  <th data-column="status">Status</th>
                  <th data-column="owner">Responsável</th>
                  <th data-column="expires">Validade</th>
                  <th data-column="preparation">Preparação</th>
                  <th data-column="cost">Custo realizado</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item) => (
                  <tr
                    className="clickable-row"
                    tabIndex={0}
                    key={item.id}
                    onClick={() => setSelected(item.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelected(item.id);
                      }
                    }}
                  >
                    <td data-column="code" className="mono">
                      {item.code}
                    </td>
                    <td data-column="certification">
                      <b>{item.name}</b>
                      <small className="table-secondary">
                        {item.type} ·{" "}
                        {item.certifying_body || "Organismo não informado"}
                      </small>
                    </td>
                    <td data-column="status">
                      <span className="status-pill" style={{color:modelColumnFor(item)?.color,borderColor:modelColumnFor(item)?.color,backgroundColor:modelColumnFor(item)?.color?`color-mix(in srgb,${modelColumnFor(item)?.color} 12%,transparent)`:undefined}}>
                        {modelColumnFor(item)?.name}
                      </span>
                    </td>
                    <td data-column="owner">{item.internal_owner}</td>
                    <td data-column="expires" className="mono">
                      {formatDate(item.expires_at)}
                    </td>
                    <td data-column="preparation">
                      <small className="table-secondary">
                        Início: {formatDate(item.preparation_start)}
                      </small>
                      <b>{item.pending_stages || 0} etapa(s) pendente(s)</b>
                    </td>
                    <td data-column="cost" className="mono">
                      {money(item.actual_cost, item.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!visibleItems.length && (
          <div className="empty">
            <FileBadge />
            <h3>Nenhuma certificação encontrada</h3>
            <p>Ajuste os filtros ou cadastre a primeira certificação.</p>
          </div>
        )}
      </section>
      {creating && (
        <CertificationForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
          }}
          onError={onError}
        />
      )}
    </section>
  );
}

function CertificationDetailView({
  id,
  onBack,
  onError,
}: {
  id: string;
  onBack: () => void;
  onError: (message: string) => void;
}) {
  const [data, setData] = useState<CertificationDetail>(),
    [adding, setAdding] = useState<"stage" | "cost" | "document" | null>(null);
  const load = useCallback(
    () =>
      api
        .certification(id)
        .then(setData)
        .catch((error) => onError(error.message)),
    [id, onError],
  );
  useEffect(() => {
    load();
  }, [load]);
  if (!data)
    return (
      <section className="card loading-card">
        Carregando detalhes da certificação…
      </section>
    );
  const c = data.certification;
  const toggleStage = async (stage: Record<string, any>) => {
    try {
      await api.updateCertificationStage(id, stage.id, {
        status: stage.status === "CONCLUÍDA" ? "PENDENTE" : "CONCLUÍDA",
      });
      load();
    } catch (error: any) {
      onError(error.message);
    }
  };
  return (
    <section className="certification-detail">
      <div className="card certification-heading">
        <div>
          <span className="mono">{c.code}</span>
          <h2>{c.name}</h2>
          <p>{c.description || "Sem descrição cadastrada."}</p>
        </div>
        <button className="button" onClick={onBack}>
          <ArrowLeft />
          Voltar para certificações
        </button>
      </div>
      <section className="detail-facts card">
        <div>
          <small>Status</small>
          <span className={`status-pill ${tone(c.status)}`}>
            {statusLabels[c.status] || c.status}
          </span>
        </div>
        <div>
          <small>Responsável</small>
          <b>{c.internal_owner}</b>
        </div>
        <div>
          <small>Validade</small>
          <b className="mono">{formatDate(c.expires_at)}</b>
        </div>
        <div>
          <small>Início da preparação</small>
          <b className="mono">{formatDate(c.preparation_start)}</b>
        </div>
        <div>
          <small>Prazo configurado</small>
          <b>{c.preparation_months} meses antes</b>
        </div>
        <div>
          <small>Custo estimado</small>
          <b className="mono">{money(c.estimated_cost, c.currency)}</b>
        </div>
      </section>
      <CertificationEntityResourcesPanel
        entityType="certification"
        entityId={c.id}
        recordName={c.name}
        showCosts={false}
        onDeleted={onBack}
        onError={onError}
      />
      <section className="card certification-section">
        <div className="section-heading">
          <div>
            <h3>
              <CalendarClock />
              Rotina de certificação
            </h3>
            <p>Etapas, prazos, responsáveis e evidências do processo.</p>
          </div>
          <button className="button" onClick={() => setAdding("stage")}>
            <Plus />
            Adicionar etapa
          </button>
        </div>
        <div className="certification-stages">
          {data.stages.map((stage) => (
            <button
              key={stage.id}
              className={`certification-stage ${stage.status === "CONCLUÍDA" ? "done" : ""}`}
              onClick={() => void toggleStage(stage)}
            >
              <span className="stage-position">{stage.position}</span>
              <div>
                <b>{stage.name}</b>
                <small>
                  {stage.owner || "Sem responsável"} · prazo{" "}
                  {formatDate(stage.due_at)}
                </small>
              </div>
              <span className={`status-pill ${tone(stage.status)}`}>
                {stageLabels[stage.status] || stage.status}
              </span>
              <ChevronRight />
            </button>
          ))}
        </div>
        {!data.stages.length && (
          <div className="empty compact">
            <CalendarClock />
            <h3>Nenhuma etapa cadastrada</h3>
            <p>Adicione a rotina desta certificação.</p>
          </div>
        )}
      </section>
      <div className="certification-columns">
        <section className="card certification-section">
          <div className="section-heading">
            <div>
              <h3>
                <WalletCards />
                Custos
              </h3>
              <p>Previsto e realizado por competência.</p>
            </div>
            <button
              className="icon-button"
              aria-label="Adicionar custo"
              onClick={() => setAdding("cost")}
            >
              <Plus />
            </button>
          </div>
          {data.costs.map((cost) => (
            <article className="record-row" key={cost.id}>
              <div>
                <b>{cost.description}</b>
                <small>
                  {cost.type} · {formatDate(cost.cost_date)}
                </small>
              </div>
              <strong className="mono">
                {money(cost.actual_value, cost.currency)}
              </strong>
            </article>
          ))}
          {!data.costs.length && (
            <p className="muted">Nenhum custo registrado.</p>
          )}
        </section>
        <section className="card certification-section">
          <div className="section-heading">
            <div>
              <h3>
                <FileText />
                Certificados e laudos
              </h3>
              <p>Documentos com validade e versão.</p>
            </div>
            <button
              className="icon-button"
              aria-label="Adicionar documento"
              onClick={() => setAdding("document")}
            >
              <Plus />
            </button>
          </div>
          {data.documents.map((document) => (
            <article className="record-row" key={document.id}>
              <FileText />
              <div>
                <b>{document.title}</b>
                <small>
                  {document.type} · versão {document.version} · validade{" "}
                  {formatDate(document.expires_at)}
                </small>
              </div>
              <span className={`status-pill ${tone(document.status)}`}>
                {document.status}
              </span>
            </article>
          ))}
          {!data.documents.length && (
            <p className="muted">Nenhum certificado ou laudo registrado.</p>
          )}
        </section>
      </div>
      <section className="card certification-section">
        <div className="section-heading">
          <div>
            <h3>
              <FlaskConical />
              Ensaios vinculados
            </h3>
            <p>Resultados de produtos relacionados à certificação.</p>
          </div>
        </div>
        {data.tests.map((test) => (
          <article className="record-row" key={test.id}>
            <FlaskConical />
            <div>
              <b>
                {test.code} · {test.product}
              </b>
              <small>
                {test.test_type} ·{" "}
                {test.laboratory || "Laboratório não informado"}
              </small>
            </div>
            <span className={`status-pill ${tone(test.status)}`}>
              {test.status}
            </span>
          </article>
        ))}
        {!data.tests.length && (
          <p className="muted">Nenhum ensaio vinculado.</p>
        )}
      </section>
      {adding && (
        <CertificationRecordForm
          kind={adding}
          certificationId={id}
          onClose={() => setAdding(null)}
          onSaved={() => {
            setAdding(null);
            load();
          }}
          onError={onError}
        />
      )}
    </section>
  );
}

function CertificationForm({
  onClose,
  onSaved,
  onError,
}: {
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    const form = new FormData(event.currentTarget);
    try {
      const created = await api.createCertification({
        name: form.get("name"),
        description: form.get("description"),
        type: form.get("type"),
        certifyingBody: form.get("certifyingBody"),
        products: form.get("products"),
        processes: form.get("processes"),
        internalOwner: form.get("internalOwner"),
        status: form.get("status"),
        expiresAt: form.get("expiresAt") || undefined,
        preparationMonths: Number(form.get("preparationMonths")),
        estimatedCost: Number(form.get("estimatedCost") || 0),
        currency: "BRL",
        notes: form.get("notes"),
      });
      try {
        await uploadCertificationAttachments(
          "certification",
          created.id,
          attachments,
        );
      } catch {
        onError(
          "A certificação foi criada, mas não foi possível enviar todos os anexos. Você pode adicioná-los ao abrir a certificação.",
        );
      }
      onSaved();
    } catch (error: any) {
      onError(error.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog title="Nova certificação" onClose={onClose}>
      <form className="modal-form compact-modal-form" onSubmit={submit}>
        <div className="grid">
          <Field label="Nome">
            <input name="name" required />
          </Field>
          <Field label="Tipo">
            <select name="type">
              <option>PROCESSO</option>
              <option>PRODUTO</option>
              <option>SELO</option>
              <option>OUTRA</option>
            </select>
          </Field>
          <Field label="Organismo certificador">
            <input name="certifyingBody" />
          </Field>
          <Field label="Responsável interno">
            <input name="internalOwner" required />
          </Field>
          <Field label="Situação">
            <select name="status">
              {Object.entries(statusLabels).map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Validade">
            <DatePicker name="expiresAt" aria-label="Validade" />
          </Field>
          <Field label="Preparação antecipada (meses)">
            <input
              type="number"
              name="preparationMonths"
              min="1"
              max="36"
              defaultValue="6"
              required
            />
          </Field>
          <Field label="Custo estimado">
            <input
              type="number"
              name="estimatedCost"
              min="0"
              step=".01"
              defaultValue="0"
            />
          </Field>
          <Field label="Produtos envolvidos" span={2}>
            <input name="products" />
          </Field>
          <Field label="Processos envolvidos" span={2}>
            <input name="processes" />
          </Field>
          <Field label="Descrição" span={2}>
            <textarea name="description" rows={2} />
          </Field>
          <Field label="Observações" span={2}>
            <textarea name="notes" rows={2} />
          </Field>
          <CertificationAttachmentPicker
            files={attachments}
            onChange={setAttachments}
            onError={onError}
          />
        </div>
        <Actions saving={saving} onClose={onClose} />
      </form>
    </Dialog>
  );
}

function CertificationRecordForm({
  kind,
  certificationId,
  onClose,
  onSaved,
  onError,
}: {
  kind: "stage" | "cost" | "document";
  certificationId: string;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const [saving, setSaving] = useState(false),
    [attachments, setAttachments] = useState<File[]>([]),
    title =
      kind === "stage"
        ? "Adicionar etapa"
        : kind === "cost"
          ? "Registrar custo"
          : "Adicionar certificado ou laudo";
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    const f = new FormData(event.currentTarget);
    try {
      let attachmentEntity: "certification" | "certification_document" =
        "certification";
      let attachmentEntityId = certificationId;
      if (kind === "stage")
        await api.addCertificationStage(certificationId, {
          name: f.get("name"),
          owner: f.get("owner"),
          department: f.get("department"),
          dueAt: f.get("dueAt") || undefined,
          status: "PENDENTE",
          description: f.get("description"),
          checklist: f.get("checklist"),
        });
      if (kind === "cost")
        await api.addCertificationCost(certificationId, {
          type: f.get("type"),
          description: f.get("description"),
          supplier: f.get("supplier"),
          costDate: f.get("costDate"),
          plannedValue: Number(f.get("plannedValue") || 0),
          actualValue: Number(f.get("actualValue") || 0),
          currency: "BRL",
          owner: f.get("owner"),
        });
      if (kind === "document") {
        const created = await api.addCertificationDocument(certificationId, {
          title: f.get("title"),
          type: f.get("type"),
          certificateNumber: f.get("certificateNumber"),
          reportNumber: f.get("reportNumber"),
          issuedAt: f.get("issuedAt") || undefined,
          expiresAt: f.get("expiresAt") || undefined,
          status: f.get("status"),
          fileName: f.get("fileName"),
          owner: f.get("owner"),
          notes: f.get("notes"),
        });
        attachmentEntity = "certification_document";
        attachmentEntityId = created.id;
      }
      try {
        await uploadCertificationAttachments(
          attachmentEntity,
          attachmentEntityId,
          attachments,
        );
      } catch {
        onError(
          "O registro foi salvo, mas não foi possível enviar todos os anexos. Você pode adicioná-los ao abrir a certificação.",
        );
      }
      onSaved();
    } catch (error: any) {
      onError(error.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog title={title} onClose={onClose}>
      <form className="modal-form compact-modal-form" onSubmit={submit}>
        <div className="grid">
          {kind === "stage" && (
            <>
              <Field label="Nome da etapa">
                <input name="name" required />
              </Field>
              <Field label="Responsável">
                <input name="owner" />
              </Field>
              <Field label="Setor">
                <input name="department" />
              </Field>
              <Field label="Prazo">
                <DatePicker name="dueAt" aria-label="Prazo" />
              </Field>
              <Field label="Descrição" span={2}>
                <textarea name="description" rows={2} />
              </Field>
              <Field label="Checklist" span={2}>
                <textarea
                  name="checklist"
                  rows={2}
                  placeholder="Um item por linha"
                />
              </Field>
            </>
          )}
          {kind === "cost" && (
            <>
              <Field label="Tipo">
                <select name="type">
                  <option>AUDITORIA</option>
                  <option>LABORATÓRIO</option>
                  <option>ENSAIO</option>
                  <option>EMISSÃO</option>
                  <option>RENOVAÇÃO</option>
                  <option>CONSULTORIA</option>
                  <option>TREINAMENTO</option>
                  <option>OUTRO</option>
                </select>
              </Field>
              <Field label="Data">
                <DatePicker
                  name="costDate"
                  required
                  aria-label="Data do custo"
                />
              </Field>
              <Field label="Valor previsto">
                <input type="number" step=".01" min="0" name="plannedValue" />
              </Field>
              <Field label="Valor realizado">
                <input type="number" step=".01" min="0" name="actualValue" />
              </Field>
              <Field label="Descrição" span={2}>
                <input name="description" required />
              </Field>
              <Field label="Fornecedor">
                <input name="supplier" />
              </Field>
              <Field label="Responsável">
                <input name="owner" />
              </Field>
            </>
          )}
          {kind === "document" && (
            <>
              <Field label="Título">
                <input name="title" required />
              </Field>
              <Field label="Tipo">
                <select name="type">
                  <option>CERTIFICADO</option>
                  <option>LAUDO</option>
                  <option>EVIDÊNCIA</option>
                  <option>OUTRO</option>
                </select>
              </Field>
              <Field label="Número do certificado">
                <input name="certificateNumber" />
              </Field>
              <Field label="Número do laudo">
                <input name="reportNumber" />
              </Field>
              <Field label="Emissão">
                <DatePicker name="issuedAt" aria-label="Data de emissão" />
              </Field>
              <Field label="Validade">
                <DatePicker name="expiresAt" aria-label="Validade" />
              </Field>
              <Field label="Situação">
                <select name="status">
                  <option>VIGENTE</option>
                  <option>A VENCER</option>
                  <option>VENCIDO</option>
                  <option>ARQUIVADO</option>
                </select>
              </Field>
              <Field label="Responsável">
                <input name="owner" />
              </Field>
              <Field label="Nome do arquivo" span={2}>
                <input
                  name="fileName"
                  placeholder="Documento ainda sem upload"
                />
              </Field>
              <Field label="Observações" span={2}>
                <textarea name="notes" rows={2} />
              </Field>
            </>
          )}
          <CertificationAttachmentPicker
            files={attachments}
            onChange={setAttachments}
            onError={onError}
          />
        </div>
        <Actions saving={saving} onClose={onClose} />
      </form>
    </Dialog>
  );
}

function Dialog({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="module-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="module-dialog-title"
      >
        <header>
          <h2 id="module-dialog-title">{title}</h2>
          <button className="icon-button" onClick={onClose} aria-label="Fechar">
            <X />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
function Field({
  label,
  wide,
  span,
  children,
}: {
  label: string;
  wide?: boolean;
  span?: 2 | 3 | 4;
  children: React.ReactNode;
}) {
  return (
    <label className={wide ? "wide" : span ? `span-${span}` : ""}>
      <span>{label}</span>
      {children}
    </label>
  );
}
function Actions({
  saving,
  onClose,
}: {
  saving: boolean;
  onClose: () => void;
}) {
  return (
    <div className="actions">
      <button type="button" className="button" onClick={onClose}>
        Cancelar
      </button>
      <button className="button primary" disabled={saving}>
        {saving ? "Salvando…" : "Salvar"}
      </button>
    </div>
  );
}
